/**
 * VRG-777 — what a single entry of `products.images` IS, now that the list can hold
 * video as well as stills, in any order.
 *
 * ── WHY THIS IS SHARED, AND WHY HERE ──────────────────────────────────────────────
 * `products.images` is a flat `json` array of absolute URL strings with no type
 * discriminator (`tables.ts`), and `images[0]` is read as THE THUMBNAIL in roughly
 * twenty places — table cells, previews, pickers, modals, and `renderOutreachPng`,
 * which fetches it SERVER-SIDE and fails outright on a video URL rather than
 * degrading. The moment slot 0 may be a clip, every one of those sites needs the
 * same "skip the video" branch, so per working-conventions §36 it is extracted on
 * the way in rather than copied twenty times and left to drift.
 *
 * It lives in `@repo/types` rather than in either app because both apps and the API
 * read the same column: the platform renders the thumbnails, the app renders the
 * hero, and the API embeds `product.images[0]` in transactional emails (see the
 * note at the foot of this file).
 *
 * ── THE EXTENSION IS THE ONLY SIGNAL, AND THAT IS NOT A SHORTCUT ─────────────────
 * The column stores bare strings. Nothing in this stack transcodes, probes, or
 * records a content type alongside them — there is no `ffmpeg`, no `sharp`, and no
 * sidecar metadata table — so there is no stored fact to consult and the URL's own
 * suffix is genuinely all there is. This is the same decision `WhereToScanPanel`'s
 * `mediaKindOf` and `ScanMediaField`'s `isVideoUrl` already took for the scan-media
 * column one panel over; this module is deliberately spelled to match them so a
 * reader meeting the third copy does not have to work out whether they differ.
 *
 * The query string and fragment are stripped first: a cache-busted or signed URL
 * (`…/clip.mp4?v=2`) ends in the query, not the extension.
 *
 * ── UNKNOWN MEANS "TREAT AS AN IMAGE", DELIBERATELY ──────────────────────────────
 * `isVideoUrl` answers one question — "must this NOT be put in an `<img>`?" — and
 * answers `false` for anything it does not positively recognise as video. That
 * asymmetry is the point. Plenty of legitimate product images already in the column
 * carry no extension at all: Shopify CDN URLs, imported `product_url` scrapes, and
 * anything served through a redirect. Requiring a recognised IMAGE extension before
 * a URL could be a thumbnail would blank those rows — a regression in the common
 * case, to guard a case that cannot occur, since the only way a video reaches this
 * column is through the upload route, which now sniffs the bytes and names the
 * stored object after the VERIFIED kind (`product-media-uploads.ts`).
 */

/** The two things a product media entry can be rendered as. */
export type ProductMediaKind = 'image' | 'video';

const VIDEO_EXTENSIONS = /\.(mp4|m4v|mov|webm)$/i;
const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp)$/i;

/** Strip query and fragment so the suffix is the real extension. */
const pathOf = (url: string): string => url.split(/[?#]/)[0];

/**
 * Whether this URL must be played rather than shown.
 *
 * False for an empty, null or unrecognised value — see the module note above on why
 * "unknown" resolves to not-video rather than to unusable.
 */
export const isVideoUrl = (url?: string | null): boolean =>
  typeof url === 'string' && VIDEO_EXTENSIONS.test(pathOf(url));

/**
 * The kind this URL positively declares, or `null` when the suffix says neither.
 *
 * Use this where rendering NOTHING is the right answer for an unrecognised value —
 * a hero slide, say, where mounting an empty `<video>` leaves the viewer staring at
 * a black box. Use `isVideoUrl` where the question is only "is an `<img>` safe".
 */
export const productMediaKindOf = (
  url?: string | null,
): ProductMediaKind | null => {
  if (typeof url !== 'string' || url.length === 0) return null;
  const path = pathOf(url);
  if (VIDEO_EXTENSIONS.test(path)) return 'video';
  if (IMAGE_EXTENSIONS.test(path)) return 'image';
  return null;
};

/**
 * The still to show wherever ONE frame has to stand for the whole product: a table
 * cell, a picker row, a preview card, an email, a poster frame.
 *
 * The first entry that is not a video — NOT `images[0]`, which may now be a clip.
 * `null` when there is no usable still at all, which is the signal for the caller's
 * own placeholder; every call site already had one, because `images` has always been
 * allowed to be empty.
 *
 * A product carrying a video is REQUIRED to carry a still as well (the product form
 * refuses the save otherwise — `ProductEditPage`), precisely so that this returns a
 * frame rather than null. The null branch is therefore for an empty list and for
 * legacy rows, not for the ordinary video case.
 */
export const getProductThumbnail = (
  images?: readonly (string | null | undefined)[] | null,
): string | null => {
  if (!images) return null;
  for (const entry of images) {
    if (typeof entry !== 'string' || entry.length === 0) continue;
    if (isVideoUrl(entry)) continue;
    return entry;
  }
  return null;
};

/**
 * Whether this media list contains any video at all.
 *
 * Used by the product form to decide whether a still is mandatory, and available to
 * any surface that wants to say "this product has a clip" without walking the list
 * itself.
 */
export const hasVideoMedia = (
  images?: readonly (string | null | undefined)[] | null,
): boolean => Boolean(images?.some(entry => isVideoUrl(entry)));

/*
 * The API's read paths were migrated in VRG-783, after shipping with this gap.
 *
 * `notification.sendgrid.service.ts` passed `product.images[0]` into seventeen
 * transactional email payloads, and `pending-ownership.drizzle.repository.ts`
 * did the same for the pending-sale list thumbnail — all of which land in an
 * `<img>`. They all read `getProductThumbnail` now.
 *
 * Kept as a note rather than deleted, because the SHAPE of the mistake recurs:
 * this column is read positionally in roughly twenty places across three
 * packages, each of them written before slot 0 could be a clip. If you are
 * adding a reader, it is `getProductThumbnail`, not `images[0]`.
 *
 * `apps/api/tests/unit/email-product-thumbnail.guard.spec.ts` enforces that for
 * `apps/api`, where it matters most and where the types cannot help at all —
 * that package runs with `strictNullChecks: false`, so `images[0]` (typed
 * `string | null`) satisfies a field typed `string` without complaint.
 */

/**
 * VRG-777 — THE ONE DECLARATION OF WHAT A PRODUCT MEDIA UPLOAD MAY BE, in the shape a
 * file picker wants (`react-dropzone`'s `accept`: MIME type -> extensions).
 *
 * ── WHY THIS IS SHARED RATHER THAN WRITTEN AT THE PICKER ──────────────────────────
 * It is shared because the un-shared version had already produced a live bug, and the
 * bug is the argument. The platform's product-image picker advertised `image/heic`
 * while the API's kind table has always refused it — and the product upload route had
 * no server-side type check at all, so a HEIC was accepted by the picker, uploaded,
 * stored, and then failed to render in every browser, with no error anywhere. Two
 * independently-authored lists of the same set, in two codebases that cannot import
 * each other's internals, drifted exactly as working-conventions §76 describes.
 *
 * So the picker reads THIS, and `apps/api`'s `PRODUCT_MEDIA_KINDS` — which carries the
 * magic-byte signatures, and must stay in the API because sniffing is a server concern —
 * is asserted against it by `product-media-accept-parity.guard.spec.ts`. Adding a format
 * to one side without the other now fails a test instead of shipping an unopenable file.
 *
 * ── WHAT IS DELIBERATELY ABSENT ───────────────────────────────────────────────────
 * HEIC (no browser renders it, and there is no transcoder in this stack to convert it),
 * SVG (a document that can carry `<script>`, served from a public storage origin), and
 * PDF (nothing on a product hero can show one). `upload-kinds.ts` argues each at length.
 */
export const PRODUCT_MEDIA_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  // `video/quicktime` is .mov's real MIME type — Safari and Finder both report it that
  // way, and there is no `video/mov`.
  'video/mp4': ['.mp4', '.m4v'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
};

export const PRODUCT_MEDIA_ACCEPT_MIME_TYPES = Object.keys(PRODUCT_MEDIA_ACCEPT);

export const PRODUCT_MEDIA_ACCEPT_EXTENSIONS = Object.values(
  PRODUCT_MEDIA_ACCEPT,
).flat();

/**
 * The refusal copy, derived from the list above so it cannot name a format the picker
 * does not accept. Upper-cased without the dot, which is how a person reads a format.
 */
export const PRODUCT_MEDIA_ACCEPT_MESSAGE = `Accepts ${PRODUCT_MEDIA_ACCEPT_EXTENSIONS.map(
  ext => ext.replace('.', '').toUpperCase(),
).join(', ')}.`;
